import React, { useState, useEffect } from 'react';
import { collection, getDocs, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { FaArrowLeft, FaArrowRight, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

function StudentEntries() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'descending' });
  const [entriesPerPage, setEntriesPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewingEntry, setViewingEntry] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);

  useEffect(() => {
    const fetchStudentHealthEntries = async () => {
      try {
        const entriesCollection = collection(db, 'studentHealthEntries');
        const entriesSnapshot = await getDocs(entriesCollection);
        const entriesList = entriesSnapshot.docs.map(doc => {
          const data = doc.data();
          const dateObj = data.date.toDate();
          return {
            id: doc.id,
            ...data,
            date: dateObj,
            formattedDate: dateObj.toLocaleDateString()
          };
        });

        entriesList.sort((a, b) => b.date.getTime() - a.date.getTime());
        setEntries(entriesList);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching student health entries: ", error);
        setLoading(false);
      }
    };

    fetchStudentHealthEntries();
  }, []);

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const handleView = (entry) => {
    setViewingEntry(entry);
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
  };

  const handleSaveEdit = async (editedEntry) => {
    try {
      const entryRef = doc(db, 'studentHealthEntries', editedEntry.id);
      await updateDoc(entryRef, { ...editedEntry, date: editedEntry.date });
      
      // Update the entry in the entries state, including formattedDate for immediate display update
      setEntries(entries.map(entry => 
        entry.id === editedEntry.id 
          ? { ...editedEntry, formattedDate: editedEntry.date.toLocaleDateString() }
          : entry
      ));
      setEditingEntry(null);
    } catch (error) {
      console.error("Error updating entry: ", error);
    }
  };
  

  const handleDelete = async (entryId) => {
    if (window.confirm('Are you sure you want to delete this entry?')) {
      try {
        await deleteDoc(doc(db, 'studentHealthEntries', entryId));
        setEntries(entries.filter(entry => entry.id !== entryId));
      } catch (error) {
        console.error("Error deleting entry: ", error);
      }
    }
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handleEntriesPerPageChange = (event) => {
    setEntriesPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => (prev * entriesPerPage < filteredAndSortedEntries.length ? prev + 1 : prev));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'ascending' ? <FaSortUp /> : <FaSortDown />;
    }
    return <FaSort />;
  };

  const filteredAndSortedEntries = entries
    .filter(entry => entry.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => {
      if (sortConfig.key === 'date') {
        return sortConfig.direction === 'ascending' 
          ? a.date.getTime() - b.date.getTime()
          : b.date.getTime() - a.date.getTime();
      } else if (sortConfig.key !== null) {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
      }
      return 0;
    });

  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = filteredAndSortedEntries.slice(indexOfFirstEntry, indexOfLastEntry);

  if (loading) {
    return <p className="text-center">Loading...</p>;
  }

  if (viewingEntry) {
    return (
      <div className="p-10 max-w-4xl mx-auto bg-white shadow-lg rounded-lg">
      <h2 className="text-4xl font-bold mb-8 text-blue-600">{viewingEntry.name}</h2>
      <div className="flex flex-col space-y-4 text-lg">
        <p className="flex items-center">
          <span className="font-semibold mr-2 text-gray-700 w-24">Date:</span> 
          <span className="bg-blue-100 px-3 py-1 rounded">{viewingEntry.formattedDate}</span>
        </p>
        <p className="flex items-center">
          <span className="font-semibold mr-2 text-gray-700 w-24">Class:</span> 
          <span className="bg-green-100 px-3 py-1 rounded">{viewingEntry.class}</span>
        </p>
        <p className="flex items-center">
          <span className="font-semibold mr-2 text-gray-700 w-24">Concern:</span> 
          <span className="bg-yellow-100 px-3 py-1 rounded">{viewingEntry.concern}</span>
        </p>
        <p className="flex items-center">
          <span className="font-semibold mr-2 text-gray-700 w-24">Status:</span> 
          <span className="bg-purple-100 px-3 py-1 rounded">{viewingEntry.status}</span>
        </p>
      </div>
      <div className="mt-8">
        <h3 className="text-2xl font-semibold mb-4 text-gray-700">Notes:</h3>
        <p className="bg-purple-100 px-3 py-1 rounded">{viewingEntry.notes || 'No notes available.'}</p>
      </div>
      <button 
        className="mt-8 px-6 py-3 bg-blue-500 text-white text-xl rounded-lg hover:bg-blue-600 transition duration-300 ease-in-out flex items-center"
        onClick={() => setViewingEntry(null)}
      >
        <FaArrowLeft className="mr-2" />
        Back to List
      </button>
    </div>
    );
  }

  if (editingEntry) {
    return (
      <div className="p-4">
        <h2 className="text-2xl font-bold mb-4">Edit Entry</h2>
        <form onSubmit={(e) => {
          e.preventDefault();
          handleSaveEdit(editingEntry);
        }}>
          <div className="mb-4">
            <label className="block mb-2">Name:</label>
            <input 
              type="text" 
              value={editingEntry.name} 
              onChange={(e) => setEditingEntry({...editingEntry, name: e.target.value})}
              className="w-full px-3 py-2 border rounded"
            />
          </div>
          <div className="mb-4">
            <label className="block mb-2">Date:</label>
            <DatePicker
              selected={editingEntry.date}
              onChange={(date) => setEditingEntry({ ...editingEntry, date })}
              className="w-full px-3 py-2 border rounded"
            />
          </div>
          <div className="mb-4">
            <label className="block mb-2">Class:</label>
            <select
              value={editingEntry.class}
              onChange={(e) => setEditingEntry({...editingEntry, class: e.target.value})}
              className="w-full px-3 py-2 border rounded"
            >
              <option value="Cubs">Cubs</option>
              <option value="Teddies">Teddies</option>
              <option value="Koalas">Koalas</option>
              <option value="Pandas 201">Pandas 201</option>
              <option value="Pandas 202">Pandas 202</option>
              <option value="Preschool 301">Preschool 301</option>
              <option value="Preschool 302">Preschool 302</option>
              <option value="Pre-K 501">Pre-K 501</option>
              <option value="Pre-K 402">Pre-K 402</option>
              <option value="Kindergarten 401">Kindergarten 401</option>
            </select>
          </div>
          <div className="mb-4">
            <label className="block mb-2">Concern:</label>
            <select
              value={editingEntry.concern}
              onChange={(e) => setEditingEntry({...editingEntry, concern: e.target.value})}
              className="w-full px-3 py-2 border rounded"
            >
              <option value="Vomiting">Vomiting</option>
              <option value="Virus">Virus</option>
              <option value="Hand/Foot in Mouth">Hand/Foot in Mouth</option>
              <option value="RSV">RSV</option>
              <option value="Covid">Covid</option>
              <option value="Strep Throat">Strep Throat</option>
              <option value="Fever">Fever</option>
              <option value="Diarrhea">Diarrhea</option>
              <option value="Eye Discharge">Eye Discharge</option>
              <option value="Rash">Rash</option>
              <option value="Sore Throat">Sore Throat</option>
              <option value="Flu">Flu</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div className="mb-4">
            <label className="block mb-2">Status:</label>
            <select
              value={editingEntry.status}
              onChange={(e) => setEditingEntry({...editingEntry, status: e.target.value})}
              className="w-full px-3 py-2 border rounded"
            >
              <option value="Sent Home">Sent Home</option>
              <option value="Parent Informed">Parent Informed</option>
            </select>
          </div>
          <div className="mb-4">
            <label className="block mb-2">Notes:</label>
            <textarea 
              value={editingEntry.notes} 
              onChange={(e) => setEditingEntry({...editingEntry, notes: e.target.value})}
              className="w-full px-3 py-2 border rounded"
            />
          </div>
          <div>
            <button type="submit" className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 mr-2">
              Save
            </button>
            <button type="button" onClick={() => setEditingEntry(null)} className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600">
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="p-4">
      <h1 className="text-3xl font-serif mb-4">Student Health Entries</h1>
      <div className="mb-4 flex justify-between items-center">
        <div className="flex items-center">
          <label className="mr-2">Entries per page:</label>
          <select
            value={entriesPerPage}
            onChange={handleEntriesPerPageChange}
            className="px-2 py-1 border border-gray-300 rounded"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
        <div className="flex items-center ml-4">
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={handleSearch}
            className="px-4 py-2 border border-gray-300 rounded"
          />
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border-gray-200 shadow-md rounded-lg">
          <thead className="bg-gray-100 text-gray-800">
            <tr>
              <th className="py-2 px-4 border-b border-gray-200 text-center">#</th>
              {['name', 'date', 'class', 'concern', 'status'].map((column) => (
                <th 
                  key={column}
                  className="py-2 px-4 border-b border-gray-200 text-center cursor-pointer"
                  onClick={() => handleSort(column)}
                >
                  <div className="flex items-center justify-center">
                    {column.charAt(0).toUpperCase() + column.slice(1)}
                    {getSortIcon(column)}
                  </div>
                </th>
              ))}
              <th className="py-2 px-4 border-b border-gray-200 text-center">Actions</th>
            </tr>
          </thead>
          <tbody className="text-gray-700">
            {currentEntries.map((entry, index) => (
              <tr key={entry.id} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                <td className="py-2 px-4 border-b border-gray-200 text-center">{index + 1 + indexOfFirstEntry}</td>
                <td className="py-2 px-4 border-b border-gray-200 text-center">{entry.name}</td>
                <td className="py-2 px-4 border-b border-gray-200 text-center">{entry.formattedDate}</td>
                <td className="py-2 px-4 border-b border-gray-200 text-center">{entry.class}</td>
                <td className="py-2 px-4 border-b border-gray-200 text-center">{entry.concern}</td>
                <td className="py-2 px-4 border-b border-gray-200 text-center">{entry.status}</td>
                <td className="py-2 px-4 border-b border-gray-200 text-center">
                  <button 
                    className="mr-2 px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                    onClick={() => handleView(entry)}
                  >
                    View
                  </button>
                  <button 
                    className="mr-2 px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                    onClick={() => handleEdit(entry)}
                  >
                    Edit
                  </button>
                  <button 
                    className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                    onClick={() => handleDelete(entry.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex justify-between mt-4">
        <div className="flex space-x-2">
          <button
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
          >
            <FaArrowLeft />
          </button>
          <button
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            onClick={handleNextPage}
            disabled={indexOfLastEntry >= filteredAndSortedEntries.length}
          >
            <FaArrowRight />
          </button>
        </div>
      </div>
    </div>
  );
}

export default StudentEntries;